import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";

import { useCreateEnvironmentLogsToken, useEnvironment } from "@/queries/environment-queries";

/**
 * Subset of the supervisor's /vm-live-usage response used by the UI.
 * The endpoint returns more fields; we only type what we render.
 */
export type DiskStats = {
    total_bytes: number;
    used_bytes: number;
};

export type VMSnapshot = {
    collected_at: string;
    vm: {
        cpu: { usage_percent: number; cores: number };
        memory: { total_bytes: number; used_bytes: number };
        disk: DiskStats;
        /** Present only in dual-disk setups (e.g. separate /mnt/data volume). */
        data_disk?: DiskStats;
        disk_io: { read_bytes: number; write_bytes: number; read_ops: number; write_ops: number };
        load: { avg1: number; avg5: number; avg15: number };
    };
};

export type DiskIORate = {
    readBytesPerSec: number;
    writeBytesPerSec: number;
    readOpsPerSec: number;
    writeOpsPerSec: number;
};

type UseVMLiveUsageResult = {
    data: VMSnapshot | undefined;
    /** Computed disk I/O rates from consecutive snapshots. Undefined until 2nd fetch. */
    diskIORate: DiskIORate | undefined;
    error: string | undefined;
    isLoading: boolean;
    /** Timestamp of the last successful fetch */
    lastUpdated: Date | undefined;
    /** Manually trigger a refetch */
    refetch: () => Promise<void>;
};

async function fetchVMStats(url: string, token: string, signal: AbortSignal): Promise<VMSnapshot> {
    const response = await fetch(url, {
        signal,
        mode: "cors",
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as VMSnapshot;
}

/**
 * Fetches VM live usage stats via TanStack Query.
 * Only fetches while `enabled` is true.
 * @param pollInterval - If set, polls at this interval (ms) while enabled. Pauses when tab is hidden.
 */
export function useVMLiveUsage(environmentId: string, enabled: boolean, pollInterval?: number): UseVMLiveUsageResult {
    const { data: environment } = useEnvironment(environmentId);
    const { data: logsToken } = useCreateEnvironmentLogsToken(enabled ? environmentId : undefined);

    const vmLiveUsageUrl = environment?.status?.environmentUrls?.vmLiveUsage;
    const canFetch = enabled && !!vmLiveUsageUrl && !!logsToken;

    const prevSnapshotRef = useRef<{ snapshot: VMSnapshot; fetchedAt: number } | null>(null);
    const diskIORateRef = useRef<DiskIORate | undefined>(undefined);

    // Reset refs when disabled so stale data doesn't leak across enable cycles
    if (!enabled) {
        prevSnapshotRef.current = null;
        diskIORateRef.current = undefined;
    }

    const query = useQuery({
        queryKey: ["vm-live-usage", environmentId, vmLiveUsageUrl],
        queryFn: ({ signal }) => fetchVMStats(vmLiveUsageUrl!, logsToken!, signal),
        enabled: canFetch,
        refetchInterval: pollInterval,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        staleTime: 10_000,
        retry: false,
        // Each snapshot must be a fresh reference so we can diff consecutive
        // snapshots for disk I/O rate calculation and update dataUpdatedAt.
        structuralSharing: false,
    });

    // Compute disk I/O rates by diffing consecutive snapshots.
    // The supervisor returns cumulative totals, so we need two data points to derive rates.
    if (query.data && query.dataUpdatedAt) {
        const prev = prevSnapshotRef.current;
        if (prev && prev.snapshot !== query.data) {
            const elapsedSec = (query.dataUpdatedAt - prev.fetchedAt) / 1000;
            if (elapsedSec > 0) {
                const prevIO = prev.snapshot.vm.disk_io;
                const curIO = query.data.vm.disk_io;
                diskIORateRef.current = {
                    readBytesPerSec: Math.max(0, (curIO.read_bytes - prevIO.read_bytes) / elapsedSec),
                    writeBytesPerSec: Math.max(0, (curIO.write_bytes - prevIO.write_bytes) / elapsedSec),
                    readOpsPerSec: Math.max(0, (curIO.read_ops - prevIO.read_ops) / elapsedSec),
                    writeOpsPerSec: Math.max(0, (curIO.write_ops - prevIO.write_ops) / elapsedSec),
                };
            }
        }
        if (!prev || prev.snapshot !== query.data) {
            prevSnapshotRef.current = { snapshot: query.data, fetchedAt: query.dataUpdatedAt };
        }
    }

    return {
        data: query.data,
        diskIORate: diskIORateRef.current,
        error: query.error?.message,
        isLoading: query.isLoading,
        lastUpdated: query.dataUpdatedAt ? new Date(query.dataUpdatedAt) : undefined,
        refetch: async () => {
            await query.refetch();
        },
    };
}
